import { Component, type ReactNode } from 'react'

// Error-boundary escape hatch for a `useSuspenseQuery` whose rejection should degrade gracefully instead of
// bubbling to the route's errorComponent: renders `fallback` (default: nothing) when a child throws.
//
// Prefer reading data with useSuspenseQuery over a plain useQuery (a non-suspense useQuery resolves mid-SSR,
// gets dehydrated with data, and hydration-mismatches the client — see CLAUDE.md). When such a read may
// legitimately reject (an optional/non-critical query, a telemetry outage) and its failure should render an
// empty or alternate UI rather than an error page, wrap it in this boundary and pair it with a <Suspense> (or
// the route's pendingComponent) for the loading state.
//
// NB: for a KNOWN domain outcome the caller renders differently (e.g. "awaiting approval"), prefer returning
// that state as a value from the procedure and branching on the data — no throw, no boundary. Reach for this
// only when the failure is genuinely exceptional and the graceful fallback is empty/alternate chrome.
export class GracefulBoundary extends Component<{ children: ReactNode; fallback?: ReactNode }, { failed: boolean }> {
  override state = { failed: false }
  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true }
  }
  override render(): ReactNode {
    return this.state.failed ? (this.props.fallback ?? null) : this.props.children
  }
}
